import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    Box,
    Card,
    Divider,
    Grid,
    Slider,
    Checkbox,
    TextField,
    Typography,
    useTheme,
    Paper,
    Tooltip as MuiTooltip,
    CircularProgress
} from '@mui/material';
import { useState, useEffect, useMemo } from 'react';
import {
    Area, Bar, CartesianGrid, ComposedChart, LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { PerformanceChart } from './components';
import {
    computeDomainsFromCumulativeReturns,
    ClusterReturnSeries, StrategyReturnSeries
} from "./calculation"; // your helper file

interface ChartDataParams extends StrategyReturnSeries {
    combinedReturns: number[];
}

type ChartPoint = {
    timestamp: string;
    prices: Record<string, number>;
    targetDistribution?: Record<string, number>;
    [key: string]: any; // This allows dynamic keys like cluster_area_${id}
};

type ChartDataResult = {
    chartData: ChartPoint[];
    priceDomain: [number, number];
    returnDomain: [number, number];
    logPriceDomain: [number, number];
    logReturnDomain: [number, number];
};

export const useChartData = ({
    timeseriesData,
    firstPrices,
    combinedReturns,
}: ChartDataParams): ChartDataResult => {
    return useMemo(() => {
        const prices: Record<string, number>[] = timeseriesData.map(data => data.prices);
        const { priceDomain, returnDomain, logPriceDomain, logReturnDomain } = computeDomainsFromCumulativeReturns(
            prices,
            combinedReturns,
            firstPrices
        );

        const chartData = buildChartPoints({
            timeseriesData
        }).map((point, i) => ({
            ...point,
            combinedReturn: combinedReturns[i]
        }));

        return {
            chartData,
            priceDomain,
            returnDomain,
            logPriceDomain,
            logReturnDomain,
        };
    }, [timeseriesData, combinedReturns]);
};

interface BuildChartPointsParams {
    timeseriesData: StrategyReturnSeries['timeseriesData'];
}

function buildChartPoints({
    timeseriesData,
}: BuildChartPointsParams): ChartPoint[] {
    return timeseriesData.map((entry, i) => {
        const point: any = {
            timestamp: entry.timestamp,
            prices: entry.prices,
            targetDistribution: entry.targetDistribution,
        };
        return point;
    });
}

type Props = {
    clustering: {
        id: string;
        name: string;
        series: string;
    };
    clusteringAssignment: string;
    cumulativeReturns: any; // type properly later
    enabledClusters: Record<number, boolean>;
    combinedReturns: any;
    strategyChartData: ChartDataResult;
    portfolioChartData: ChartDataResult;
    benchmarkChartData: ChartDataResult;
}

const StrategyPerformanceChart = ({
                                        clustering,
                                        clusteringAssignment,
                                        cumulativeReturns,
                                        enabledClusters,
                                        combinedReturns,
                                        strategyChartData,
                                        portfolioChartData,
                                        benchmarkChartData
                                    }: Props) => {
    const theme = useTheme();

    return (
        <Box mt={4} height={400}>
            <Typography variant="subtitle2" gutterBottom>
                Performance — {clustering.name}
            </Typography>
            <PerformanceChart
                enabledClusters={enabledClusters}
                showCombinedStrategy={combinedReturns.at(-1) !== 1}
                data={strategyChartData}
                portfolioData={portfolioChartData}
                benchmarkData={benchmarkChartData}
            />
        </Box>
    );
};

export default StrategyPerformanceChart;
